<template>
  <div class="">
    <div class="flex flex-1 flex-col">
      <main>
        <div class="py-6">
          <div class="mx-auto max-w-7xl sm:px-6 lg:px-8">
            <!-- Add Start Over button if not on restart step -->
            <div class="flex justify-end mb-4" v-if="form.step !== 'restart'">
              <button
                @click="startOver"
                class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-protocase-500"
              >
                <ArrowPathIcon class="w-5 h-5 mr-2" />
                Start Over
              </button>
            </div>
            
            <div class="pb-4">
              <div class="h-96 rounded-lg lg:px-8">
                <form
                  class="space-y-8 divide-y divide-gray-200"
                  method="POST"
                  @submit.prevent="sendEmail"
                  v-if="!form.formSubmitted"
                >
                  <div class="space-y-8 divide-y divide-gray-200">
                    <div>
                      <div
                        class="flex flex-col items-center justify-center py-8 bg-gray-50"
                        v-if="form.step == 'restart'"
                      >
                        <!-- Remove min-h-[50vh] -->
                        <div class="max-w-3xl w-full px-4">
                          <!-- Remove duplicate heading since it's now in App.vue -->
                          <p class="text-gray-600 text-center mb-8">
                            Choose an option below to get started with your project
                          </p>

                          <div class="grid grid-cols-1 gap-6">
                            <div
                              v-for="option in options"
                              :key="option.title"
                              @click="handleOptionClick(option)"
                              class="group relative bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100"
                              :class="{'cursor-pointer': option.value !== 'Instant Quoter'}"
                            >
                              <div class="flex items-center space-x-4">
                                <div class="flex-shrink-0">
                                  <component
                                    :is="option.icon"
                                    class="h-8 w-8 text-protocase-600 group-hover:text-protocase-700"
                                    aria-hidden="true"
                                  />
                                </div>
                                <div class="flex-1">
                                  <h2
                                    class="text-xl font-semibold text-gray-900 group-hover:text-protocase-600"
                                  >
                                    {{ option.title }}
                                  </h2>
                                  <p class="mt-2 text-gray-600">
                                    {{ option.description }}
                                  </p>
                                </div>
                                <div class="flex-shrink-0">
                                  <ChevronRightIcon
                                    class="h-5 w-5 text-gray-400 group-hover:text-protocase-600"
                                    aria-hidden="true"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div v-if="form.step == 'start'">
                        <h3
                          class="text-lg text-center font-medium leading-6 text-gray-900 sm:text-2xl"
                        >
                          Let's get started. What are you looking to quote?
                        </h3>

                        <p class="mt-6 p-2 rounded-md text-sm">
                          Our team is here to get your designs quoted accurately and
                          quickly. We strive to provide your quote within one business
                          day. Fill out the form below to submit your quote request.
                        </p>

                        <div
                          class="flex flex-col items-center justify-center py-8 bg-gray-50"
                          v-if="!form.quoteType"
                        >
                          <!-- Remove min-h-[50vh] -->
                          <div class="max-w-3xl w-full px-4">
                            <!-- Remove duplicate heading since it's now in App.vue -->
                            <p class="text-gray-600 text-center mb-8">
                              Choose an option below to get started with your project
                            </p>

                            <div class="grid grid-cols-1 gap-6">
                              <div
                                v-for="option in options"
                                @click="form.quoteType = option.value"
                                class="group relative bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 cursor-pointer"
                              >
                                <div class="flex items-center space-x-4">
                                  <div class="flex-shrink-0">
                                    <component
                                      :is="option.icon"
                                      class="h-8 w-8 text-protocase-600 group-hover:text-protocase-700"
                                      aria-hidden="true"
                                    />
                                  </div>
                                  <div class="flex-1">
                                    <h2
                                      class="text-xl font-semibold text-gray-900 group-hover:text-protocase-600"
                                    >
                                      {{ option.value }}
                                    </h2>
                                    <p class="mt-2 text-gray-600">
                                      {{ option.description }}
                                    </p>
                                  </div>
                                  <div class="flex-shrink-0">
                                    <ChevronRightIcon
                                      class="h-5 w-5 text-gray-400 group-hover:text-protocase-600"
                                      aria-hidden="true"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <KeepAlive>
                          <ReOrder v-if="form.quoteType == 'Reorder'" />
                        </KeepAlive>

                        <KeepAlive>
                          <NewDesign v-if="form.quoteType == 'New Design'" />
                        </KeepAlive>
                      </div>

                      <KeepAlive>
                        <MaterialFinishes
                          v-if="
                            form.step == 'materialFinishes' &&
                            form.quoteType == 'New Design'
                          "
                        />
                      </KeepAlive>

                      <KeepAlive>
                        <MoreInfo v-if="form.step == 'projectTimeline'" />
                      </KeepAlive>

                      <KeepAlive>
                        <Contact
                          v-if="form.step == 'contactInfo'"
                          :sendEmail="checkForm"
                        />
                      </KeepAlive>

                      <!-- Show modal popup when fields are missing -->
                      <TransitionRoot as="template" :show="form.open">
                        <Dialog as="div" class="relative z-10" @close="form.open = false">
                          <TransitionChild
                            as="template"
                            enter="ease-out duration-300"
                            enter-from="opacity-0"
                            enter-to="opacity-100"
                            leave="ease-in duration-200"
                            leave-from="opacity-100"
                            leave-to="opacity-0"
                          >
                            <div
                              class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                            />
                          </TransitionChild>

                          <div class="fixed inset-0 z-10 overflow-y-auto">
                            <div
                              class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
                            >
                              <TransitionChild
                                as="template"
                                enter="ease-out duration-300"
                                enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enter-to="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leave-from="opacity-100 translate-y-0 sm:scale-100"
                                leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                              >
                                <DialogPanel
                                  class="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6"
                                >
                                  <div>
                                    <button
                                      @click="form.open = false"
                                      ref="cancelButtonRef"
                                      v-if="form.success == true"
                                      class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100"
                                    >
                                      <CheckIcon
                                        class="h-6 w-6 text-green-600"
                                        aria-hidden="true"
                                      />
                                    </button>
                                    <button
                                      @click="form.open = false"
                                      ref="cancelButtonRef"
                                      v-if="form.success == false"
                                      class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100"
                                    >
                                      <XMarkIcon
                                        class="h-6 w-6 text-red-600"
                                        aria-hidden="true"
                                      />
                                    </button>

                                    <div class="mt-3 text-center sm:mt-5">
                                      <DialogTitle as="p">
                                        <!-- add missing fields -->
                                        <template v-if="form.success == false">
                                          <p class="font-semibold">
                                            Please fill out the following fields:
                                          </p>
                                          <p
                                            v-for="field in form.missingFields"
                                            :key="field"
                                            class="text-xs items-center text-left leading-6 text-red-900"
                                          >
                                            * {{ field }}
                                          </p>
                                        </template>
                                        <!-- add success message -->
                                        <template v-if="success == true">
                                          <p>
                                            Thank you for your submission. We will be in
                                            touch shortly.
                                          </p>
                                        </template>
                                      </DialogTitle>
                                    </div>
                                  </div>
                                </DialogPanel>
                              </TransitionChild>
                            </div>
                          </div>
                        </Dialog>
                      </TransitionRoot>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- overlay loading spinner if loading is true-->
    <div
      v-if="loading"
      class="fixed inset-0 z-50 flex items-center justify-center overflow-auto bg-white"
    >
      <div class="flex flex-col items-center justify-center">
        <!-- animate spinner with background white -->
        <svg
          class="animate-spin h-10 w-10 text-dark"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042.
            1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    </div>
  </div>
</template>

<script setup>
import Button from "./components/formComponents/Button.vue";
import LandingPage from "./components/LandingPage.vue";
import ReOrder from "./components/ReOrder.vue";
import PocketBase from "pocketbase";
import { inject, reactive, provide, ref, onMounted, watch } from "vue";
import NewDesign from "./components/NewDesign.vue";
import MaterialFinishes from "./components/MaterialFinishes.vue";
import RadioButtonGroupHeadless from "./components/formComponents/RadioButtonGroupHeadless.vue";
import MoreInfo from "./components/MoreInfo.vue";
import Contact from "./components/Contact.vue";

import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import {
  Bars3BottomLeftIcon,
  CalendarIcon,
  FolderIcon,
  HomeIcon,
  UsersIcon,
  XMarkIcon,
  CheckIcon,
  ChatBubbleLeftRightIcon,
  PlayCircleIcon,
  ChevronRightIcon,
  CalculatorIcon,
  ArrowPathIcon,
  PlusCircleIcon,
} from "@heroicons/vue/24/outline";

const form = reactive({
  step: "restart",
  quoteType: "Reorder",
  formSubmitted: false,
  LaserMarkingSheetMetal: [],
  reorderJobs: [
    {
      jobNumber: "",
      partNameDesc: "",
      Quantity: "",
      file: null,
      Description: "",
    },
  ],
  documentation: [],
  missingFields: [],
  open: false,
});

const loading = ref(false);

// reset form
const resetForm = () => {
  form.step = "start";
  form.quoteType = "";
  form.fileCad = "";
  form.LaserMarkingSheetMetal = [];
  form.reorderJobs = [
    {
      jobNumber: "",
      partNameDesc: "",
      Quantity: "",
      file: null,
      Description: "",
    },
  ];
  form.documentation = [];
  form.missingFields = [];
  form.open = false;
};

const handleOptionClick = (option) => {
  if (option.value === 'Instant Quoter') {
    window.location.href = 'https://www.protocase.com/price/protocase-instant-quoter';
  } else {
    form.step = 'start';
    form.quoteType = option.value;
  }
};

// Update options array
const options = [
  {
    title: 'Instant Quoter',
    value: 'Instant Quoter',
    description: 'Get an instant quote using our online tool',
    icon: CalculatorIcon,
  },
  {
    title: 'Reorder',
    value: 'Reorder', 
    description: 'Place a new order for previously ordered parts',
    icon: ArrowPathIcon,
  },
  {
    title: 'New Design',
    value: 'New Design',
    description: 'Submit a new design for quoting',
    icon: PlusCircleIcon,
  }
];

// use .env file for PocketBase API
const pb = new PocketBase(import.meta.env.VITE_POCKETBASE_API);

const id = ref(null);

// onmounted fetch id https://configuration-calculator.45drives.com/api/v1/proto-rfq-id/generate
onMounted(async () => {
  id.value = await fetch("https://forms.45drives.com/api/v1/quote-id/generate")
    .then((response) => response.json())
    .then((data) => data["id"]);
});

// send form data to PocketBase API with files uploaded
const sendEmail = async () => {
  const data = new FormData();

  // Reorder Jobs data

  if (form.quoteType == "Reorder") {
    data.append("processed", false);

    data.append("princesss_id", id.value);

    data.append("reOrderJobs", JSON.stringify(form.reorderJobs));
    // append file from reOrderJobs array
    for (let i = 0; i < form.reorderJobs.length; i++) {
      data.append("files", form.reorderJobs[i].file);
    }

    const projectTimeline = new Map();

    projectTimeline.set(
      "Please tell us more about your project, its application and your requirements. This information will help expedite your quote, as our Engineering & Design Services team will be able to make the most informed decisions based on your specific needs.",
      form.descriptionAboutProject
    );

    projectTimeline.set("What is your preferred leadtime", form.leadTime);

    projectTimeline.set(
      "Can we quote with substitutions in order to achieve the fastest manufacturing turnaround possible ?",
      form.quoteSubstitutions
    );

    projectTimeline.set(
      "Can we make minor design modifications in order to match our manufacturing capabilities ? (All changes will be outlined to you and approved before production)",
      form.minorDesignModifications
    );

    projectTimeline.set(
      "Are there any critical aspects of your design that we should note ?",
      form.criticalAspects
    );
    if (form.criticalAspects == "Yes") {
      projectTimeline.set(
        "Please describe the critical aspects of your design",
        form.descriptionCriticalAspects
      );
    }

    // Does your design need to be compliant to the following programs ?
    projectTimeline.set(
      "Does your design need to be compliant to the following programs ?",
      form.compliantPrograms
    );

    projectTimeline.set(
      "What is your timeline, or ideal in -hand date ?",
      form.dateValue
    );

    projectTimeline.set(
      "If you have multiple parts, would you like those parts to be considered as an assembly ?",
      form.multiplrPartsAssembly
    );

    projectTimeline.set(
      "Do you require any of the following documentation ?",
      form.documentation
    );

    projectTimeline.set(
      "Does your design require Certificate of Conformance or Inspection Services ?",
      form.inspectionCoC
    );

    if (form.inspectionCoC === "Yes") {
      projectTimeline.set(
        "What type of inspection service do you require ?",
        form.typeOfCertificateOfConformance
      );
      if (
        form.typeOfCertificateOfConformance ===
          "Custom - Certificate of Conformance(C of C)" ||
        form.typeOfCertificateOfConformance == "Basic Station Custom" ||
        form.typeOfCertificateOfConformance == "Premium AS9102 Custom"
      ) {
        projectTimeline.set(
          "Please describe your custom inspection requirements",
          form.descriptionCustomInspection
        );
      }
      if (
        form.typeOfCertificateOfConformance === "Basic Station" ||
        form.typeOfCertificateOfConformance == "Premium AS9102" ||
        form.typeOfCertificateOfConformance == "Basic Station Custom" ||
        form.typeOfCertificateOfConformance == "Premium AS9102 Custom"
      ) {
        projectTimeline.set(
          "What is the frequency of inspection ?",
          form.frequencyOfInspection
        );
      }
    }

    // set domain
    projectTimeline.set("Domain", window.location.hostname);

    // convert projectTimeline Map to JSON.
    const projectTimelineJSON = JSON.stringify(Object.fromEntries(projectTimeline));
    data.append("projectTimeline", projectTimelineJSON);

    // append contactInfo to JSON
    const contactInfo = new Map();
    contactInfo.set("FirstName", form.FirstName);
    contactInfo.set("LastName", form.LastName);
    contactInfo.set("Email", form.Email);
    contactInfo.set("Phone", form.Phone);
    contactInfo.set("Company", form.Company);
    contactInfo.set("Industry", form.Industry);
    contactInfo.set("Country", form.Country.country);
    contactInfo.set("State", form.State);
    // if state is california, ask zip code
    if (form.State == "California") {
      contactInfo.set("ZipCode", form.ZipCode);
    }
    contactInfo.set("Message", form.Message);
    contactInfo.set("contactBy", form.contactBy);

    // convert contactInfo Map to JSON.
    const contactInfoJSON = JSON.stringify(Object.fromEntries(contactInfo));
    data.append("contactInfo", contactInfoJSON);

    console.log("test");
    try {
      loading.value = true;
      const record = await pb.collection("reorderJobs").create(data);

      //  if record is created successfully
      if (record.id) {
        // read domain where app is embedded

        window.top.location.href =
          "https://www.protocase.com/price/thank-you-rfq.php?recordId=" +
          record.id +
          "&type=reorderJobs";
      }

      console.log("Success"); // Success
      loading.value = false;
      success.value = true;
    } catch (error) {
      console.log(error);
      // redirect to error page
      window.parent.location.href = "https://protocase.com/error.php";
    }
  }

  // New Design data
  if (form.quoteType == "New Design") {
    data.append("princesss_id", "WN" + id.value);

    data.append("processed", false);

    data.append("fileCad", form.fileCad);

    data.append("designTypeofEnclosure", form.designTypeofEnclosure);

    // basicsInfo data
    const basicsInfo = new Map();
    basicsInfo.set("Do you have 3D CAD files?", form.fileCad);
    // set domain
    basicsInfo.set("Domain", window.location.hostname);
    // store fileName
    basicsInfo.set("CadFileName", form.NewDesignfileName);

    // Is this a new revision to a part we previously quoted?
    if (form.fileCad == "Yes") {
      basicsInfo.set(
        "Is this a new revision to a part we previously quoted?",
        form.revisedPart
      );
      if (form.revisedPart == "Yes") {
        basicsInfo.set("Quantity", form.Quantity);
        basicsInfo.set("Previous Quote Number", form.previousQuoteNumber);
      } else {
        basicsInfo.set("Quantity", form.Quantity);
        basicsInfo.set(
          "Is this similar to a part we previously ordered?",
          form.similarToPreviouslyOrdered
        );
        if (form.similarToPreviouslyOrdered == "Yes") {
          basicsInfo.set("Previous Quote Number", form.previousQuoteNumber);
        }
      }
    } else {
      basicsInfo.set("How would you like to proceed?", form.cadsoftware);
      // Describe your requirements
      basicsInfo.set("Describe your requirements", form.designRequirements);
      // Upload your File rough sketch
      basicsInfo.set("Rough file or sketch name", form.fileSketch);

      basicsInfo.set("Quantity", form.Quantity);
      // Do you have a part or component you can send for our Design Services team to refer to?
      basicsInfo.set(
        "Do you have a part or component you can send for our Design Services team to refer to?",
        form.designPartComponent
      );
    }

    // convert basicsInfo Map to JSON string
    data.append("basicsInfo", JSON.stringify(Object.fromEntries(basicsInfo)));

    // if fileCad is Yes

    if (form.fileCad == "Yes") {
      data.append("revisedPart", form.revisedPart);
      if (form.revisedPart == "Yes") {
        data.append("quantity", form.Quantity);
        data.append("previousQuoteNumber", form.previousQuoteNumber);
      } else {
        data.append("quantity", form.Quantity);
        data.append("similarToPreviouslyOrdered", form.similarToPreviouslyOrdered);
        if (form.similarToPreviouslyOrdered == "Yes") {
          data.append("previousQuoteNumber", form.previousQuoteNumber);
        }
      }
    }

    // if designTypeofEnclosure is Sheet Metal
    if (form.designTypeofEnclosure === "Sheet Metal") {
      // Sheet Metal data in key value pairs without using FormData
      const sheetMetal = new Map();

      // add data to sheetMetal Map
      sheetMetal.set(
        "What type of enclosure or part are you looking to get designed and manufactured?",
        form.designTypeofEnclosure
      );
      sheetMetal.set(
        "Material Type",
        form.typeMaterialSheetMetal + "(" + form.typeMaterialSheetMetalOther + ")"
      );
      sheetMetal.set("Material Thickness ", form.typeMaterialSheetMetalSize);
      sheetMetal.set(
        "Finish Type",
        form.typeMaterial +
          "(" +
          form.typeMaterialFinishType +
          " " +
          form.otherPowderCoat +
          ")"
      );
      sheetMetal.set(
        "Does your design require welding? ",
        form.NewDesignSheetMetalWelding
      );
      sheetMetal.set("Do you require graphics?", form.DoYouRequireGrpahics);
      sheetMetal.set("Which type of graphics do you require?", form.reqGraphics);
      sheetMetal.set(
        "Which type of digital print do you require? *",
        form.NewDesignSheetMetalDigitalPrint
      );

      sheetMetal.set(
        "Does your design require Permanent Marking?",
        form.reqPermanentMarking
      );
      sheetMetal.set(
        "Which permanent marking are you looking for?",
        form.NewDesignSheetMetalPermanentMarking
      );
      if (form.NewDesignSheetMetalPermanentMarking == "Laser Marking") {
        sheetMetal.set(
          "Which types of Laser Marking do you want quoted?",
          form.LaserMarkingSheetMetal
        );
      }
      sheetMetal.set("Is your artwork finalized?", form.artworkFinalized);
      sheetMetal.set("Artwork file name?", form.artworkFinalizedSheetMetalfileName);
      sheetMetal.set(
        "Did you generate a CAD template from our Template Generator to create your design?",
        form.usedProtocaseDesigner
      );

      // replace undefined with empty string

      // convert sheetMetal Map to JSON
      const sheetMetalJSON = JSON.stringify(Object.fromEntries(sheetMetal));
      data.append("designTypeofEnclosureJSON", sheetMetalJSON);
    }

    // if designTypeofEnclosure is CNC Machined
    if (form.designTypeofEnclosure === "CNC Machined") {
      // CNC Machined in key value pairs without using FormData
      const Cnc = new Map();

      // add data to Cnc Map
      Cnc.set(
        "What type of enclosure or part are you looking to get designed and manufactured?",
        form.designTypeofEnclosure
      );
      Cnc.set(
        "Material Type",
        form.typeMaterialCnc +
          "(" +
          form.typeMaterialCncDependent +
          ")" +
          "(" +
          form.typeMaterialCncOther +
          ")"
      );
      Cnc.set(
        "Finish Type",
        form.typeMaterialFinishCNC +
          "(" +
          form.typeMaterialFinishType +
          ")" +
          "(" +
          form.otherPowderCoat +
          ")"
      );
      // Does your design require Permanent Marking?
      Cnc.set("Does your design require Permanent Marking?", form.reqPermanentMarking);
      Cnc.set(
        "Which permanent marking are you looking for?",
        form.NewDesignSheetMetalPermanentMarking
      );

      // typeMaterialFinishAdditionStainlessSteel
      if (form.typeMaterialCnc == "Stainless Steel") {
        Cnc.set("Additional Finish Type", form.typeMaterialFinishAdditionStainlessSteel);
      }

      // typeMaterialFinishAdditionAluminum
      if (form.typeMaterialCnc == "Aluminum") {
        Cnc.set("Additional Finish Type", form.typeMaterialFinishAdditionAluminum);
      }

      // convert Cnc Map to JSON
      const CncJSON = JSON.stringify(Object.fromEntries(Cnc));
      data.append("designTypeofEnclosureJSON", CncJSON);
    }

    // if designTypeofEnclosure is Bus Bar
    if (form.designTypeofEnclosure === "Bus Bar") {
      const busBar = new Map();

      busBar.set(
        "What type of enclosure or part are you looking to get designed and manufactured?",
        form.designTypeofEnclosure
      );

      // add data to sheetMetal Map
      busBar.set(
        "Material Type",
        form.typeBusBarMaterial + "(" + form.typeBusBarMaterialOther + ")"
      );
      busBar.set("Material Thickness", form.materialThickness);

      busBar.set("Do you require Tin Plating ?", form.tinPlating);

      busBar.set("Bare Metal Finish", form.bareMetal);

      // convert busBar Map to JSON
      const busBarJSON = JSON.stringify(Object.fromEntries(busBar));
      data.append("designTypeofEnclosureJSON", busBarJSON);
    }

    // if designTypeofEnclosure is Customizable Diecast Enclosure
    if (form.designTypeofEnclosure === "Customizable Diecast Enclosure") {
      const CustomizableDiecastEnclosure = new Map();

      CustomizableDiecastEnclosure.set(
        "What type of enclosure or part are you looking to get designed and manufactured?",
        form.designTypeofEnclosure
      );

      // add data to sheetMetal Map
      CustomizableDiecastEnclosure.set(
        "Material Type ",
        form.typeDiecast +
          "(" +
          form.typeDiecastSubSelection +
          ")" +
          "(" +
          form.typeDiecastOther +
          ")"
      );
      CustomizableDiecastEnclosure.set(
        "Finish Type",
        form.typeMaterialFinish + "(" + form.typeMaterialFinishType + ")"
      );

      if (form.finishType == "Other") {
        CustomizableDiecastEnclosure.set(
          "Please indicate the powdercoat color you require",
          form.otherPowderCoat
        );
      }

      if (form.reqGraphics == "Yes") {
        CustomizableDiecastEnclosure.set("Do you require graphics?", form.reqGraphics);
        CustomizableDiecastEnclosure.set(
          "Which type of graphics do you require? ",
          form.reqGraphicsType
        );
        // if reqGraphicsType is Digital Printing
        if (
          form.reqGraphicsType == "Digital Printing" ||
          form.reqGraphicsType == "Quote for Both"
        ) {
          CustomizableDiecastEnclosure.set(
            "Which type of digital print do you require?",
            form.DigitalPrint
          );
        }
      }

      // if required permanent marking is yes
      if (form.reqPermanentMarking == "Yes") {
        CustomizableDiecastEnclosure.set(
          "DOes your design require Permanent Marking?",
          form.reqPermanentMarking
        );
        CustomizableDiecastEnclosure.set(
          "Which permanent marking are you looking for?",
          form.reqPermanentMarkingType
        );
        // if reqPermanentMarkingType is Laser Marking
        if (form.reqPermanentMarkingType == "Laser Marking") {
          CustomizableDiecastEnclosure.set(
            "Which types of Laser Marking do you want quoted?",
            form.DiecastLaserMarking
          );
        }
      }
      // Is your artwork finalized? *
      CustomizableDiecastEnclosure.set(
        "Is your artwork finalized?",
        form.artworkFinalized
      );
      CustomizableDiecastEnclosure.set(
        "Artwork file name?",
        form.customizableDiecastGraphicName
      );
      // Yes
      // No
      // Please upload your CAD file *
      // if required graphics is yes

      CustomizableDiecastEnclosure.set("bareMetalFinish", form.bareMetal);
      // customizableDiecastCadFile

      // convert busBar Map to JSON
      const CustomizableDiecastEnclosureJSON = JSON.stringify(
        Object.fromEntries(CustomizableDiecastEnclosure)
      );
      data.append("designTypeofEnclosureJSON", CustomizableDiecastEnclosureJSON);
    }

    // if designTypeofEnclosure is 3D Printing
    if (form.designTypeofEnclosure == "3D Printing") {
      const threeDPrinting = new Map();

      threeDPrinting.set(
        "What type of enclosure or part are you looking to get designed and manufactured?",
        form.designTypeofEnclosure
      );
      threeDPrinting.set("Material Type", form.type3Dprinting);
      // if type3Dprinting is Other
      if (form.type3Dprinting == "Other") {
        threeDPrinting.set("Other Material Type", form.type3DprintingOther);
      }
      threeDPrinting.set("Color Filament", form.colorFilament);
      // if colorFilament is Other
      if (form.colorFilament == "Other") {
        threeDPrinting.set("Color Filament slected as other", form.otherColorFilament);
      }
      threeDPrinting.set("layerHeight", form.layerHeight);
      // if layerHeight is Other
      if (form.layerHeight == "Other") {
        threeDPrinting.set("otherLayerHeight", form.otherLayerHeight);
      }
      threeDPrinting.set("surfaceFinish", form.surfaceFinish);
      threeDPrinting.set("infillPercentage", form.infillPercentage);

      // convert busBar Map to JSON
      const threeDPrintingJSON = JSON.stringify(Object.fromEntries(threeDPrinting));

      data.append("designTypeofEnclosureJSON", threeDPrintingJSON);
    }

    // if designTypeofEnclosure is Gasket
    if (form.designTypeofEnclosure == "Gasket") {
      const gasket = new Map();

      gasket.set(
        "What type of enclosure or part are you looking to get designed and manufactured?",
        form.designTypeofEnclosure
      );
      gasket.set("typeGasketMaterial", form.typeGasketMaterial);
      gasket.set("Other type of Gasket", form.typeGasketMaterialOther);

      // convert busBar Map to JSON.
      const gasketJSON = JSON.stringify(Object.fromEntries(gasket));
      data.append("designTypeofEnclosureJSON", gasketJSON);
    }

    // if designTypeofEnclosure is Other
    if (form.designTypeofEnclosure == "Other") {
      const other = new Map();

      other.set(
        "What type of enclosure or part are you looking to get designed and manufactured?",
        form.designTypeofEnclosure
      );
      other.set(
        "Please tell us about your design, and what you require. Our team will assess this information and your design file!",
        form.OtherTypeofEnclosure
      );

      // convert busBar Map to JSON.
      const otherJSON = JSON.stringify(Object.fromEntries(other));
      data.append("designTypeofEnclosureJSON", otherJSON);
    }

    if (form.artworkFinalized == "Yes") {
      // if designTypeofEnclosure is Sheet Metal
      if (form.designTypeofEnclosure === "Sheet Metal") {
        data.append("graphicFile", form.artworkFinalizedSheetMetal);
      }
      // if designTypeofEnclosure is Customizable Diecast Enclosure
      if (form.designTypeofEnclosure === "Customizable Diecast Enclosure") {
        data.append("graphicFile", form.customizableDiecastGraphic);
      }
      // if designTypeofEnclosure is 3D Printing
      if (form.designTypeofEnclosure === "3D Printing") {
        data.append("graphicFile", form.threeDPrintingGraphic);
      }
      // if designTypeofEnclosure is Gasket
      if (form.designTypeofEnclosure === "Gasket") {
        data.append("graphicFile", form.gasketGraphic);
      }
      // if designTypeofEnclosure is Other
      if (form.designTypeofEnclosure === "Other") {
        data.append("graphicFile", form.otherGraphic);
      }
    }

    // if fileCAD is NO
    if (form.fileCad == "No") {
      const useProtocaseDesignServices = new Map();

      useProtocaseDesignServices.set("How would you like to proceed?", form.cadsoftware);

      useProtocaseDesignServices.set(
        "Describe your requirements",
        form.designRequirements
      );

      useProtocaseDesignServices.set("File Name", form.NewDesignNoCadfileName);

      useProtocaseDesignServices.set("Quantity", form.Quantity);

      useProtocaseDesignServices.set(
        "Do you have a part or component you can send for our Design Services team to refer to?",
        form.designPartComponent
      );

      // convert busBar Map to JSON.
      const useProtocaseDesignServicesJSON = JSON.stringify(
        Object.fromEntries(useProtocaseDesignServices)
      );

      // add NewDesignNoCadfile to data
      data.append("useProtocaseDesignServices", useProtocaseDesignServicesJSON);

      data.append("newDesignNoCadfile", form.NewDesignNoCadfile);
    }

    const projectTimeline = new Map();

    projectTimeline.set(
      "Please tell us more about your project, its application and your requirements. This information will help expedite your quote, as our Engineering & Design Services team will be able to make the most informed decisions based on your specific needs.",
      form.descriptionAboutProject
    );

    projectTimeline.set("What is your preferred leadtime", form.leadTime);

    projectTimeline.set(
      "Can we quote with substitutions in order to achieve the fastest manufacturing turnaround possible ?",
      form.quoteSubstitutions
    );

    projectTimeline.set(
      "Can we make minor design modifications in order to match our manufacturing capabilities ? (All changes will be outlined to you and approved before production)",
      form.minorDesignModifications
    );

    projectTimeline.set(
      "Are there any critical aspects of your design that we should note ?",
      form.criticalAspects
    );
    if (form.criticalAspects == "Yes") {
      projectTimeline.set(
        "Please describe the critical aspects of your design",
        form.descriptionCriticalAspects
      );
    }

    projectTimeline.set(
      "Does your design need to be compliant to the following programs ?",
      form.compliantPrograms
    );

    projectTimeline.set(
      "What is your timeline, or ideal in -hand date ?",
      form.dateValue
    );

    projectTimeline.set(
      "If you have multiple parts, would you like those parts to be considered as an assembly ?",
      form.multiplrPartsAssembly
    );

    projectTimeline.set(
      "Do you require any of the following documentation ?",
      form.documentation
    );

    projectTimeline.set(
      "Does your design require Certificate of Conformance or Inspection Services ?",
      form.inspectionCoC
    );

    if (form.inspectionCoC == "Yes") {
      projectTimeline.set(
        "What type of inspection service do you require ?",
        form.typeOfCertificateOfConformance
      );
      if (
        form.typeOfCertificateOfConformance ==
          "Custom - Certificate of Conformance(C of C)" ||
        form.typeOfCertificateOfConformance == "Basic Station Custom" ||
        form.typeOfCertificateOfConformance == "Premium AS9102 Custom"
      ) {
        projectTimeline.set(
          "Please describe your custom inspection requirements",
          form.descriptionCustomInspection
        );
      }
      if (
        form.typeOfCertificateOfConformance == "Basic Station" ||
        form.typeOfCertificateOfConformance == "Premium AS9102" ||
        form.typeOfCertificateOfConformance == "Basic Station Custom" ||
        form.typeOfCertificateOfConformance == "Premium AS9102 Custom"
      ) {
        projectTimeline.set(
          "What is the frequency of inspection ?",
          form.frequencyOfInspection
        );
      }
    }

    // set domain
    projectTimeline.set("Domain", window.location.hostname);

    // convert projectTimeline Map to JSON.
    const projectTimelineJSON = JSON.stringify(Object.fromEntries(projectTimeline));
    data.append("projectTimeline", projectTimelineJSON);

    data.append("newDesign3Dfile", form.NewDesignfile);

    // append contactInfo to JSON
    const contactInfo = new Map();

    contactInfo.set("FirstName", form.FirstName);
    contactInfo.set("LastName", form.LastName);
    contactInfo.set("Email", form.Email);
    contactInfo.set("Phone", form.Phone);
    contactInfo.set("Company", form.Company);
    contactInfo.set("Industry", form.Industry);
    contactInfo.set("Country", form.Country.country);
    contactInfo.set("State", form.State);
    // if state is california ask Zip code
    if (form.State == "California") {
      contactInfo.set("ZipCode", form.ZipCode);
    }
    contactInfo.set("Message", form.Message);
    contactInfo.set(
      "How would you prefer to be contacted about this quote?",
      form.contactBy
    );

    // convert contactInfo Map to JSON.
    const contactInfoJSON = JSON.stringify(Object.fromEntries(contactInfo));
    data.append("contactInfo", contactInfoJSON);

    try {
      loading.value = true;

      console.log(data);

      const record = await pb.collection("newDesignJobs").create(data);

      //  if record is created successfully
      if (record.id) {
        window.top.location.href =
          "https://www.protocase.com/price/thank-you-rfq.php?recordId=" +
          record.id +
          "&type=newDesign";
      }

      console.log("Success"); // Success
      loading.value = false;

      // missingFields.value = '';
    } catch (error) {
      console.log(error);
      window.parent.location.href = "https://protocase.com/error.php";
    }
  }
};

// check if form is valid
const checkForm = () => {
  sendEmail();
  // collect all data from console
};

const sidebarOpen = ref(false);
const open = ref(false);
const success = ref(false);
// const missingFields = reactive('');

setInterval(
  () =>
    window.parent.postMessage(
      JSON.stringify({
        height: document.body.scrollHeight,
        width: document.body.scrollWidth,
        app: "rfq",
      }),
      "*"
    ),
  500
);

provide("form", form);

// Add startOver function
const startOver = () => {
  form.step = "restart";
  form.quoteType = "";
  form.fileCad = "";
  form.LaserMarkingSheetMetal = [];
  form.reorderJobs = [
    {
      jobNumber: "",
      partNameDesc: "",
      Quantity: "",
      file: null,
      Description: "",
    },
  ];
  form.documentation = [];
  form.missingFields = [];
  form.open = false;
  
  // Reset all form fields to their default values
  Object.keys(form).forEach(key => {
    if (typeof form[key] === 'string') form[key] = '';
    if (Array.isArray(form[key])) form[key] = [];
    if (typeof form[key] === 'boolean') form[key] = false;
  });
  
  // Reset specific values
  form.step = "restart";
  form.formSubmitted = false;
};
</script>
